/**
 * useApiMutation Hooks
 *
 * Type-safe React Query mutation hooks for POST, PATCH, and DELETE requests.
 * Includes automatic cache invalidation based on resource relationships.
 */

import { useMutation, type UseMutationOptions, type UseMutationResult, useQueryClient } from '@tanstack/react-query';
import type { paths } from '@access-cms/api-types';

import { ApiRequestError, del, patch, post, put } from '../client';
import type { QueryKeyBase } from '../queryKeys';
import { getInvalidationKeys } from '../queryKeys';
import type { ApiPaths, PathParams, QueryParams, RequestBody, SuccessResponse } from '../types';
import { getResourceType } from '../types';

// =============================================================================
// Type Utilities
// =============================================================================

/**
 * Paths that have a POST method
 */
type PostPaths = {
  [P in ApiPaths]: paths[P] extends { post: object } ? P : never;
}[ApiPaths];

/**
 * Paths that have a PATCH method
 */
type PatchPaths = {
  [P in ApiPaths]: paths[P] extends { patch: object } ? P : never;
}[ApiPaths];

/**
 * Paths that have a PUT method
 */
type PutPaths = {
  [P in ApiPaths]: paths[P] extends { put: object } ? P : never;
}[ApiPaths];

/**
 * Paths that have a DELETE method
 */
type DeletePaths = {
  [P in ApiPaths]: paths[P] extends { delete: object } ? P : never;
}[ApiPaths];

/**
 * Type for additional invalidation keys
 */
type InvalidationKeyList = readonly QueryKeyBase[];

/**
 * Makes all properties of T optional, including nested objects.
 * Used for path params that can be split between static config and dynamic mutation calls.
 */
type PartialPathParams<T> = T extends object ? { [K in keyof T]?: T[K] } : T;

/**
 * Safely merge two partial path param objects.
 * Handles cases where either param might be undefined or not an object.
 */
function mergePathParams<T>(
  staticParams: PartialPathParams<T> | undefined,
  dynamicParams: PartialPathParams<T> | undefined,
): T {
  return {
    ...(staticParams ?? {}),
    ...(dynamicParams ?? {}),
  } as T;
}

// =============================================================================
// POST Hook
// =============================================================================

interface UseApiCreateOptions<P extends PostPaths> {
  /**
   * Additional query keys to invalidate on success
   */
  invalidateKeys?: InvalidationKeyList;

  /**
   * Callback on error
   */
  onError?: (error: ApiRequestError) => void;

  /**
   * Callback on successful mutation
   */
  onSuccess?: (data: SuccessResponse<P, 'post'>) => void;

  /**
   * Additional React Query mutation options
   */
  options?: Omit<
    UseMutationOptions<SuccessResponse<P, 'post'>, ApiRequestError, RequestBody<P, 'post'>>,
    'mutationFn' | 'onSuccess' | 'onError'
  >;

  /**
   * Parent resource ID for nested resources (used for cache invalidation)
   */
  parentId?: string;

  /**
   * The API path for the POST request
   */
  path: P;

  /**
   * Static path parameters (e.g., { contractId: '123' } for nested resources)
   */
  pathParams?: PathParams<P, 'post'>;

  /**
   * Query parameters to include
   */
  queryParams?: QueryParams<P, 'post'>;
}

/**
 * Hook for creating a new resource (POST request)
 *
 * @example
 * ```tsx
 * const createUser = useApiCreate({
 *   path: '/api/v1/users',
 *   onSuccess: (newUser) => {
 *     toast.success('User created successfully');
 *   },
 * });
 *
 * // In component
 * createUser.mutate({ email: 'new@example.com', userType: 'CONTRACTOR' });
 *
 * // For nested resources
 * const addRosterMember = useApiCreate({
 *   path: '/api/v1/contracts/{contractId}/roster',
 *   pathParams: { contractId },
 *   parentId: contractId,
 * });
 * ```
 */
export function useApiCreate<P extends PostPaths>(
  config: UseApiCreateOptions<P>,
): UseMutationResult<SuccessResponse<P, 'post'>, ApiRequestError, RequestBody<P, 'post'>> {
  const { path, pathParams, queryParams, invalidateKeys, parentId, onSuccess, onError, options } = config;
  const queryClient = useQueryClient();

  // Determine resource type for automatic cache invalidation
  const resourceType = getResourceType(path as string);

  return useMutation<SuccessResponse<P, 'post'>, ApiRequestError, RequestBody<P, 'post'>>({
    mutationFn: async (body) => {
      return post(path, body, {
        pathParams,
        queryParams,
      });
    },
    onError,
    onSuccess: (data) => {
      // Invalidate relevant caches
      const keysToInvalidate = [
        ...(resourceType ? getInvalidationKeys(resourceType, parentId) : []),
        ...(invalidateKeys ?? []),
      ];

      keysToInvalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });

      onSuccess?.(data);
    },
    ...options,
  });
}

// =============================================================================
// PATCH Hook
// =============================================================================

interface UseApiUpdateOptions<P extends PatchPaths> {
  /**
   * Additional query keys to invalidate on success
   */
  invalidateKeys?: InvalidationKeyList;

  /**
   * Callback on error
   */
  onError?: (error: ApiRequestError) => void;

  /**
   * Callback on successful mutation
   */
  onSuccess?: (data: SuccessResponse<P, 'patch'>) => void;

  /**
   * Additional React Query mutation options
   */
  options?: Omit<
    UseMutationOptions<
      SuccessResponse<P, 'patch'>,
      ApiRequestError,
      { body: RequestBody<P, 'patch'>; pathParams?: PartialPathParams<PathParams<P, 'patch'>> }
    >,
    'mutationFn' | 'onSuccess' | 'onError'
  >;

  /**
   * Parent resource ID for nested resources
   */
  parentId?: string;

  /**
   * The API path for the PATCH request
   */
  path: P;

  /**
   * Static path parameters. Can be partially provided here and completed per-mutation.
   * The static and dynamic params are merged at mutation time.
   */
  pathParams?: PartialPathParams<PathParams<P, 'patch'>>;

  /**
   * Query parameters to include
   */
  queryParams?: QueryParams<P, 'patch'>;
}

/**
 * Hook for updating a resource (PATCH request)
 *
 * @example
 * ```tsx
 * const updateUser = useApiUpdate({
 *   path: '/api/v1/users/{id}',
 * });
 *
 * // In component - pass pathParams per mutation
 * updateUser.mutate({
 *   pathParams: { id: userId },
 *   body: { status: 'ACTIVE' },
 * });
 *
 * // With static pathParams
 * const updateContract = useApiUpdate({
 *   path: '/api/v1/contracts/{id}',
 *   pathParams: { id: contractId },
 * });
 *
 * updateContract.mutate({ body: { contractStatus: 'ACTIVE' } });
 * ```
 */
export function useApiUpdate<P extends PatchPaths>(
  config: UseApiUpdateOptions<P>,
): UseMutationResult<
  SuccessResponse<P, 'patch'>,
  ApiRequestError,
  { body: RequestBody<P, 'patch'>; pathParams?: PartialPathParams<PathParams<P, 'patch'>> }
> {
  const {
    path,
    pathParams: staticPathParams,
    queryParams,
    invalidateKeys,
    parentId,
    onSuccess,
    onError,
    options,
  } = config;
  const queryClient = useQueryClient();

  const resourceType = getResourceType(path as string);

  return useMutation<
    SuccessResponse<P, 'patch'>,
    ApiRequestError,
    { body: RequestBody<P, 'patch'>; pathParams?: PartialPathParams<PathParams<P, 'patch'>> }
  >({
    mutationFn: async ({ body, pathParams }) => {
      const mergedParams = mergePathParams<PathParams<P, 'patch'>>(staticPathParams, pathParams);

      return patch(path, body, {
        pathParams: mergedParams,
        queryParams,
      });
    },
    onError,
    onSuccess: (data) => {
      const keysToInvalidate = [
        ...(resourceType ? getInvalidationKeys(resourceType, parentId) : []),
        ...(invalidateKeys ?? []),
      ];

      keysToInvalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });

      onSuccess?.(data);
    },
    ...options,
  });
}

// =============================================================================
// Bodyless POST Action Hook
// =============================================================================

interface UseApiActionOptions<P extends PostPaths> {
  /**
   * Additional query keys to invalidate on success
   */
  invalidateKeys?: InvalidationKeyList;

  /**
   * Callback on error
   */
  onError?: (error: ApiRequestError) => void;

  /**
   * Callback on successful mutation
   */
  onSuccess?: (data: SuccessResponse<P, 'post'>) => void;

  /**
   * Additional React Query mutation options
   */
  options?: Omit<
    UseMutationOptions<SuccessResponse<P, 'post'>, ApiRequestError, PartialPathParams<PathParams<P, 'post'>>>,
    'mutationFn' | 'onSuccess' | 'onError'
  >;

  /**
   * Parent resource ID for nested resources
   */
  parentId?: string;

  /**
   * The API path for the POST action
   */
  path: P;

  /**
   * Static path parameters. Can be partially provided here and completed per-mutation.
   */
  pathParams?: PartialPathParams<PathParams<P, 'post'>>;

  /**
   * Query parameters to include
   */
  queryParams?: QueryParams<P, 'post'>;
}

/**
 * Hook for POST actions that do not accept caller-provided fields.
 * Sends an empty JSON object so Fastify receives a valid JSON request body.
 *
 * @example
 * ```tsx
 * const resendInvitation = useApiAction({
 *   path: '/api/v1/contracts/{contractId}/roster/{userId}/resend-invitation',
 *   pathParams: { contractId },
 * });
 *
 * resendInvitation.mutate({ userId });
 * ```
 */
export function useApiAction<P extends PostPaths>(
  config: UseApiActionOptions<P>,
): UseMutationResult<SuccessResponse<P, 'post'>, ApiRequestError, PartialPathParams<PathParams<P, 'post'>>> {
  const {
    path,
    pathParams: staticPathParams,
    queryParams,
    invalidateKeys,
    parentId,
    onSuccess,
    onError,
    options,
  } = config;
  const queryClient = useQueryClient();

  const resourceType = getResourceType(path as string);

  return useMutation<SuccessResponse<P, 'post'>, ApiRequestError, PartialPathParams<PathParams<P, 'post'>>>({
    mutationFn: async (pathParams) => {
      const mergedParams = mergePathParams<PathParams<P, 'post'>>(staticPathParams, pathParams);

      return post(path, {} as RequestBody<P, 'post'>, {
        pathParams: mergedParams,
        queryParams,
      });
    },
    onError,
    onSuccess: (data) => {
      const keysToInvalidate = [
        ...(resourceType ? getInvalidationKeys(resourceType, parentId) : []),
        ...(invalidateKeys ?? []),
      ];

      keysToInvalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });

      onSuccess?.(data);
    },
    ...options,
  });
}

// =============================================================================
// PUT Hook
// =============================================================================

interface UseApiUpsertOptions<P extends PutPaths> {
  /**
   * Additional query keys to invalidate on success
   */
  invalidateKeys?: InvalidationKeyList;

  /**
   * Callback on error
   */
  onError?: (error: ApiRequestError) => void;

  /**
   * Callback on successful mutation
   */
  onSuccess?: (data: SuccessResponse<P, 'put'>) => void;

  /**
   * Additional React Query mutation options
   */
  options?: Omit<
    UseMutationOptions<
      SuccessResponse<P, 'put'>,
      ApiRequestError,
      { body: RequestBody<P, 'put'>; pathParams?: PartialPathParams<PathParams<P, 'put'>> }
    >,
    'mutationFn' | 'onSuccess' | 'onError'
  >;

  /**
   * Parent resource ID for nested resources
   */
  parentId?: string;

  /**
   * The API path for the PUT request
   */
  path: P;

  /**
   * Static path parameters. Can be partially provided here and completed per-mutation.
   */
  pathParams?: PartialPathParams<PathParams<P, 'put'>>;

  /**
   * Query parameters to include
   */
  queryParams?: QueryParams<P, 'put'>;
}

/**
 * Hook for upserting a resource (PUT request).
 *
 * @example
 * ```tsx
 * const saveFnInfo = useApiUpsert({
 *   path: '/api/v1/applications/{id}/foreign-national-information',
 *   pathParams: { id: applicationId },
 * });
 *
 * saveFnInfo.mutate({ body: { cmsOrganization: '...' } });
 * ```
 */
export function useApiUpsert<P extends PutPaths>(
  config: UseApiUpsertOptions<P>,
): UseMutationResult<
  SuccessResponse<P, 'put'>,
  ApiRequestError,
  { body: RequestBody<P, 'put'>; pathParams?: PartialPathParams<PathParams<P, 'put'>> }
> {
  const {
    path,
    pathParams: staticPathParams,
    queryParams,
    invalidateKeys,
    parentId,
    onSuccess,
    onError,
    options,
  } = config;
  const queryClient = useQueryClient();

  const resourceType = getResourceType(path as string);

  return useMutation<
    SuccessResponse<P, 'put'>,
    ApiRequestError,
    { body: RequestBody<P, 'put'>; pathParams?: PartialPathParams<PathParams<P, 'put'>> }
  >({
    mutationFn: async ({ body, pathParams }) => {
      const mergedParams = mergePathParams<PathParams<P, 'put'>>(staticPathParams, pathParams);

      return put(path, body, {
        pathParams: mergedParams,
        queryParams,
      });
    },
    onError,
    onSuccess: (data) => {
      const keysToInvalidate = [
        ...(resourceType ? getInvalidationKeys(resourceType, parentId) : []),
        ...(invalidateKeys ?? []),
      ];

      keysToInvalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });

      onSuccess?.(data);
    },
    ...options,
  });
}

// =============================================================================
// DELETE Hook
// =============================================================================

interface UseApiDeleteOptions<P extends DeletePaths> {
  /**
   * Additional query keys to invalidate on success
   */
  invalidateKeys?: InvalidationKeyList;

  /**
   * Callback on error
   */
  onError?: (error: ApiRequestError) => void;

  /**
   * Callback on successful mutation
   */
  onSuccess?: (data: SuccessResponse<P, 'delete'>) => void;

  /**
   * Additional React Query mutation options
   */
  options?: Omit<
    UseMutationOptions<SuccessResponse<P, 'delete'>, ApiRequestError, PartialPathParams<PathParams<P, 'delete'>>>,
    'mutationFn' | 'onSuccess' | 'onError'
  >;

  /**
   * Parent resource ID for nested resources
   */
  parentId?: string;

  /**
   * The API path for the DELETE request
   */
  path: P;

  /**
   * Static path parameters. Can be partially provided here and completed per-mutation.
   * The static and dynamic params are merged at mutation time.
   */
  pathParams?: PartialPathParams<PathParams<P, 'delete'>>;
}

/**
 * Hook for deleting a resource (DELETE request)
 *
 * @example
 * ```tsx
 * const deleteUser = useApiDelete({
 *   path: '/api/v1/users/{id}',
 *   onSuccess: () => {
 *     toast.success('User deleted');
 *     navigate('/users');
 *   },
 * });
 *
 * // In component
 * deleteUser.mutate({ id: userId });
 *
 * // For nested resources
 * const removeRosterMember = useApiDelete({
 *   path: '/api/v1/contracts/{contractId}/roster/{userId}',
 *   pathParams: { contractId },
 *   parentId: contractId,
 * });
 *
 * removeRosterMember.mutate({ userId: memberId });
 * ```
 */
export function useApiDelete<P extends DeletePaths>(
  config: UseApiDeleteOptions<P>,
): UseMutationResult<SuccessResponse<P, 'delete'>, ApiRequestError, PartialPathParams<PathParams<P, 'delete'>>> {
  const { path, pathParams: staticPathParams, invalidateKeys, parentId, onSuccess, onError, options } = config;
  const queryClient = useQueryClient();

  const resourceType = getResourceType(path as string);

  return useMutation<SuccessResponse<P, 'delete'>, ApiRequestError, PartialPathParams<PathParams<P, 'delete'>>>({
    mutationFn: async (pathParams) => {
      const mergedParams = mergePathParams<PathParams<P, 'delete'>>(staticPathParams, pathParams);

      return del(path, { pathParams: mergedParams });
    },
    onError,
    onSuccess: (data) => {
      const keysToInvalidate = [
        ...(resourceType ? getInvalidationKeys(resourceType, parentId) : []),
        ...(invalidateKeys ?? []),
      ];

      keysToInvalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });

      onSuccess?.(data);
    },
    ...options,
  });
}
